import React, { useRef, useEffect, useState } from 'react';
import {
  OverlayTooltip,
  Placement,
} from '../../design-system/components/overlay';
import type { TruncatedTextWithAutoTooltipProps } from './types';

/**
 * This component can be used to show a tooltip when the text is truncated.
 */

const TruncatedTextWithAutoTooltip: React.FC<TruncatedTextWithAutoTooltipProps> = ({
  text,
  className = '',
  placement = Placement.Bottom,
  tooltipClassName = '',
  children,
  redirectUrl,
  telHref,
  tooltipText,
}) => {
  const textRef = useRef<HTMLSpanElement | HTMLAnchorElement | null>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        // Check if text is truncated by comparing scrollWidth with clientWidth
        const isTruncated = element.scrollWidth > element.clientWidth;
        setIsTextTruncated(isTruncated);
      }
    };

    checkTruncation();

    // Recheck on window resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkTruncation, 150); // Debounced resize check
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    // Use ResizeObserver if available for more accurate detection
    let resizeObserver: ResizeObserver | null = null;
    const node = textRef.current;
    if (
      typeof window !== 'undefined' &&
      window.ResizeObserver &&
      node
    ) {
      try {
        resizeObserver = new ResizeObserver(() => {
          setTimeout(checkTruncation, 100);
        });
        resizeObserver.observe(node);
      } catch (error) {
        // Fallback if ResizeObserver fails - silently continue
      }
    }

    return () => {
      clearTimeout(resizeTimeout);
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
    // Include telHref / redirectUrl so span ↔ <a> swap re-subscribes; include
    // isTextTruncated so plain node ↔ OverlayTooltip wrap does not leave a stale observer.
  }, [text, redirectUrl, telHref, isTextTruncated]);

  const truncationStyles: React.CSSProperties = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    minWidth: 0,
    maxWidth: '100%',
  };

  let textElement: JSX.Element;
  if (redirectUrl) {
    textElement = (
      <a
        ref={textRef as React.RefObject<HTMLAnchorElement>}
        href={redirectUrl}
        target="_blank"
        rel="noreferrer"
        className={className}
        style={truncationStyles}
      >
        {children || text}
      </a>
    );
  } else if (telHref) {
    textElement = (
      <a
        ref={textRef as React.RefObject<HTMLAnchorElement>}
        href={telHref}
        className={className}
        style={truncationStyles}
      >
        {children || text}
      </a>
    );
  } else {
    textElement = (
      <span
        ref={textRef}
        className={className}
        style={truncationStyles}
      >
        {children || text}
      </span>
    );
  }

  if (tooltipText || isTextTruncated) {
    return (
      <OverlayTooltip
        text={tooltipText || text}
        placement={placement}
        className={tooltipClassName}
      >
        {textElement}
      </OverlayTooltip>
    );
  }

  return textElement;
};

export default TruncatedTextWithAutoTooltip;
